<template>
  <b-modal
    id="admin-create-cart-modal"
    :title="trans('new_shopping_cart')"
    size="lg"
    :ok-title="trans('create')"
    :ok-disabled="busy || !canCreate"
    :cancel-title="trans('cancel')"
    @shown="onShown"
    @ok="onCreate"
  >
    <p>
      {{ trans('admin_new_shopping_cart') }}
    </p>

    <h5>{{ trans('client') }}</h5>
    <b-row>
      <b-col class="pr-0">
        <b-form-group :label="trans('first_name') + ' *'">
          <b-form-input
            name="first_name"
            v-model="recipient_first_name"
            :state="isRecipientFirstNameValid"
            required
            :disabled="busy"
          />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group :label="trans('last_name') + ' *'">
          <b-form-input
            name="last_name"
            v-model="recipient_last_name"
            :state="isRecipientLastNameValid"
            required
            :disabled="busy"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="pr-0">
        <b-form-group :label="trans('email') + ' *'">
          <b-form-input
            name="email"
            type="email"
            v-model="recipient_email"
            :state="isRecipientEmailValid"
            required
            :disabled="busy"
          />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group :label="trans('mobile')">
          <b-form-input
            name="mobile"
            v-model="recipient_mobile"
            :state="isRecipientMobileValid"
            :formatter="phoneFormatter"
            :disabled="busy"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <br/>

    <h5>{{ trans('form') }}</h5>
    <b-form-group>
      <model-treeselect
        :organization="organization"
        :url="`/organization/${this.organization.id}/checkout_form/list?per_page=100`"
        :multiple="false"
        v-model="preset_checkout_form_id"
        :placeholder="trans('form')"
        :disabled="busy"
      ></model-treeselect>
      <b-form-text>
        {{ trans('admin_cart_form_blank') }}
      </b-form-text>
    </b-form-group>
    <br/>

    <h5>{{ trans('expiration') }}</h5>
    <b-row>
      <b-col class="pr-0">
        <b-form-group :label="trans('date')">
          <datetime
            v-model="cart_expired_date"
            class="form-control"
            :config="dateOptions"
          />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group :label="trans('time')">
          <datetime
            v-model="cart_expired_time"
            class="form-control"
            :config="timeOptions"
          />
        </b-form-group>
        <div v-if="isExpired" class="d-block text-danger text-right pr-2">
          {{ trans('expired') }}
        </div>
        <div v-if="!isExpired" class="d-block text-success text-right pr-2">
          {{ cartExpiredAt.fromNow() }}
        </div>
      </b-col>
    </b-row>
    <br/>

    <payment-options
      :organization="organization"
      :disabled="busy"
      v-model="preset_payment_method"
      type="cart"
      :deposit="preset_deposit"
      @deposit="preset_deposit = $event"
    ></payment-options>

    <p v-if="busy">
      <i class="fa fa-fw fa-circle-notch fa-spin"></i>
      {{ trans('please_wait') }}
    </p>
    <p v-if="error" class="text-danger">
      {{ error }}
    </p>
  </b-modal>
</template>

<script>
  import Datetime from 'vue-bootstrap-datetimepicker'
  import ModelTreeselect from './../../../common/components/ModelTreeselect'
  import PaymentOptions from './../payment/PaymentOptions'

  function isEmpty(value) {
    return ((value||'')+'').trim().length == 0
  }

  export default {
    props: {
      organization: Object,
    },
    components: {
      Datetime,
      ModelTreeselect,
      PaymentOptions,
    },
    data() {
      return {
        trans: trans,
        moment: moment,
        numeral: numeral,
        isEmpty: isEmpty,
        busy: false,
        error: null,
        cart_expired_date: moment().format('LL'),
        cart_expired_time: moment().add(10, 'minutes').format('LT'),
        recipient_first_name: null,
        recipient_last_name: null,
        recipient_email: null,
        recipient_mobile: null,
        preset_checkout_form_id: null,
        preset_payment_method: null,
        preset_deposit: null,
        dateOptions: {
          locale: window.lang,
          format: 'LL',
        },
        timeOptions: {
          locale: window.lang,
          format: 'LT',
          stepping: 5,
        },
      }
    },
    computed: {
      isRecipientFirstNameValid() {
        return !isEmpty(this.recipient_first_name)
      },
      isRecipientLastNameValid() {
        return !isEmpty(this.recipient_last_name)
      },
      isRecipientEmailValid() {
        if (isEmpty(this.recipient_email)) return false
        return _.validateEmail(this.recipient_email)
      },
      isRecipientMobileValid() {
        if (isEmpty(this.recipient_mobile)) return null
        return _.validatePhone(this.recipient_mobile)
      },
      cartExpiredAt() {
        return moment(this.cart_expired_date + ' ' + this.cart_expired_time, 'LLL')
      },
      isExpired() {
        return moment().isAfter(this.cartExpiredAt)
      },
      canCreate() {
        return this.isRecipientFirstNameValid && this.isRecipientLastNameValid &&
          this.isRecipientEmailValid && this.isRecipientMobileValid !== false &&
          !this.isExpired
      },
    },
    methods: {
      phoneFormatter(phone) {
        return _.formatPhone(phone)
      },
      onShown() {
        this.busy = false
        this.error = null
      },
      onCreate(e) {
        e.preventDefault()
        let data = {
          recipient: {
            first_name: this.recipient_first_name,
            last_name: this.recipient_last_name,
            name: _.compact([this.recipient_first_name, this.recipient_last_name]).join(' '),
            email: this.recipient_email,
            mobile: this.recipient_mobile
          },
          preset_checkout_form_id: this.preset_checkout_form_id,
          preset_payment_method: this.preset_payment_method,
          preset_deposit: this.preset_deposit,
          expired_at: this.cartExpiredAt.toISOString()
        }
        let url = `/org/${this.organization.id}/cart/create`
        this.busy = true
        this.error = null
        axios.post(url, data)
        .then(response => {
          //this.busy = false
          //this.$bvModal.hide('admin-create-cart-modal')
          this.$emit('create', response.data)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
    },
    mounted() {
      window.adminCreateCartModal = this
    },
  }
</script>